/* eslint-disable new-cap */
import { jsPDF } from 'jspdf';
import React, { useMemo } from 'react';

import { EButtonSize, EButtonType } from 'types/enums/EButton';
import { IButton } from 'types/interfaces/IButton';

import {
  getButtonSizeStyles,
  getButtonTypeStyles,
} from 'utils/getButtonStyles';
import notify from 'utils/notify';

import './Button.css';

interface IButtonInvoice
  extends IButton,
    React.ComponentPropsWithoutRef<'div'> {
  title: string;
  category: string;
  price: string;
  paymentDate: string;
  firstName: string;
  lastName: string;
  email: string;
}

const toSafeString = (value: unknown, fallback = ''): string => {
  if (value instanceof Date) return value.toLocaleDateString('de-DE');
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return fallback;
};

const ButtonInvoice = ({
  title,
  category,
  price,
  paymentDate,
  firstName,
  lastName,
  email,
  label = undefined,
  buttonType = EButtonType.PRIMARY,
  buttonSize = EButtonSize.REGULAR,
  disabled = false,
  ...rest
}: IButtonInvoice) => {
  const safeTitle = useMemo(
    () =>
      toSafeString(title, 'Welt des Erbens').replace(/[/\\?%*:|"<>]/g, ' -'),
    [title],
  );
  const safeCategory = useMemo(() => toSafeString(category), [category]);
  const safePrice = useMemo(() => toSafeString(price, '0,00'), [price]);
  const safePaymentDate = useMemo(
    () => toSafeString(paymentDate),
    [paymentDate],
  );
  const safeFirstName = useMemo(() => toSafeString(firstName), [firstName]);
  const safeLastName = useMemo(() => toSafeString(lastName), [lastName]);
  const safeEmail = useMemo(() => toSafeString(email), [email]);

  const handleClick = () => {
    try {
      const name = `${safeFirstName} ${safeLastName}`;

      const doc = new jsPDF();
      doc.setFontSize(18);
      doc.setLineWidth(0.5);
      doc.text('Rechnung', 10, 15);

      // Invoice header
      doc.setFontSize(12);
      doc.setTextColor(0);
      doc.setFont('times', 'normal');

      doc.text('Datum:', 160, 15);
      doc.text(safePaymentDate, 180, 15);

      doc.text('Welt des Erbens WDE GmbH', 10, 35);
      doc.text('Moorweg 2', 10, 40);
      doc.text('24582 Bordesholm', 10, 45);
      doc.text('Deutschland', 10, 50);
      doc.line(10, 55, 200, 55);

      // Invoice recipient
      doc.text('Rechnung an:', 10, 65);
      doc.text(name, 10, 70);
      doc.text(safeEmail, 10, 75);
      doc.line(10, 80, 200, 80);

      // Table for product information
      doc.text('Produkt', 10, 90);
      doc.text('Menge', 120, 90);
      doc.text('MwSt.', 140, 90);
      doc.text('Preis', 170, 90);
      doc.line(10, 95, 200, 95);
      doc.text(`${safeCategory}: ${safeTitle}`, 10, 100);
      doc.text('1', 120, 100);
      doc.text('19%', 140, 100);
      doc.text(`${safePrice} EUR`, 170, 100);

      // Closing words
      doc.text('Vielen Dank für Ihren Einkauf bei Welt des Erbens.', 10, 250);
      doc.text(
        'Bei Fragen oder Anmerkungen stehen wir Ihnen gerne zur Verfügung.',
        10,
        255,
      );
      doc.text('Mit freundlichen Grüßen,', 10, 275);
      doc.text('Welt des Erbens WDE GmbH', 10, 280);

      const blob = doc.output('blob');
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Rechnung - ${safeTitle}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      notify('Die Rechnung wurde erfolgreich heruntergeladen.', 'success');
    } catch (error) {
      console.error('PDF-Generierung fehlgeschlagen:', error);
      notify('Die Rechnung konnte nicht heruntergeladen werden.', 'error');
    }
  };

  return (
    <button
      type='button'
      className={`button ${
        buttonSize === EButtonSize.SMALL ? 'button-small' : ''
      }`}
      disabled={disabled}
      onClick={handleClick}
    >
      <div
        className={`${getButtonTypeStyles({
          buttonType,
        })} ${getButtonSizeStyles({
          label,
          buttonSize,
        })} flex items-center justify-center text-center no-underline rounded-full transition-all ease-in-out duration-300 ${
          disabled && 'pointer-events-none opacity-50'
        }`}
        {...rest}
      >
        {label && <span className={`${label ? 'inline' : ''}`}>{label}</span>}
      </div>
    </button>
  );
};

export default ButtonInvoice;
